import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { Suspense, useEffect, useState } from "react";
import { lazyWithRetry } from "@/lib/lazy-retry";
import { RotateCcw } from "lucide-react";

import appCss from "../styles.css?url";
import interWoff2 from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import archivoWoff2 from "@fontsource/archivo-black/files/archivo-black-latin-400-normal.woff2?url";
import { CityProvider } from "@/context/CityContext";
import { LanguageProvider, useLanguage as useLang } from "@/i18n/LanguageContext";
import { AutoLocaleRedirect } from "@/i18n/AutoLocaleRedirect";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { Toaster } from "@/components/ui/sonner";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { PageViewTracker } from "@/hooks/use-page-view-tracker";
import { TicketNotFound } from "@/components/TicketNotFound";
// Deferred, post-hydration UI — lazy-loaded so it stays out of the
// main critical bundle. lazyWithRetry survives stale-deploy chunk 404s.
const ReferralCapture = lazyWithRetry(() => import("@/components/ReferralCapture").then(m => ({ default: m.ReferralCapture })));
const LocationPermissionPrompt = lazyWithRetry(() => import("@/components/LocationPermissionPrompt").then(m => ({ default: m.LocationPermissionPrompt })));
const SupportReplyToast = lazyWithRetry(() => import("@/components/SupportReplyToast").then(m => ({ default: m.SupportReplyToast })));
const RealtimeNotificationToaster = lazyWithRetry(() => import("@/components/RealtimeNotificationToaster").then(m => ({ default: m.RealtimeNotificationToaster })));
const RealtimeContentSync = lazyWithRetry(() => import("@/components/RealtimeContentSync").then(m => ({ default: m.RealtimeContentSync })));
const OnboardingGate = lazyWithRetry(() => import("@/components/OnboardingGate").then(m => ({ default: m.OnboardingGate })));
const Footer = lazyWithRetry(() => import("@/components/Footer").then(m => ({ default: m.Footer })));
import { getSmmNotFoundFallback } from "@/lib/smm-routes";
import { resolvePartnerRedirect } from "@/lib/partner-subdomain";





function hardReload() {
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {}
  if ("caches" in window) {
    caches.keys().then((names) => {
      names.forEach((n) => caches.delete(n));
    });
  }
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister());
    });
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_", String(Date.now()));
  window.location.href = url.toString();
}


function NotFoundComponent() {
  return (
    <LanguageProvider>
      <NotFoundRouter />
    </LanguageProvider>
  );
}

function NotFoundRouter() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // /tickets index was removed — tickets now live as cards in /do
  if (pathname === "/tickets" || pathname === "/tickets/") {
    if (typeof window !== "undefined") window.location.replace("/do");
    return null;
  }
  const smmFallback = getSmmNotFoundFallback(pathname);
  if (smmFallback) {
    if (typeof window !== "undefined") window.location.replace(smmFallback.target);
    return (
      <div className="flex min-h-dvh items-center justify-center px-4 text-center">
        <div className="max-w-md">
          <h1 className="text-xl font-bold">Opening SMM {smmFallback.label}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{smmFallback.reason}</p>
          <a className="mt-4 inline-flex rounded-full bg-foreground px-5 py-2 text-xs font-bold uppercase tracking-wider text-background" href={smmFallback.target}>
            Continue
          </a>
        </div>
      </div>
    );
  }
  const ticketMatch = pathname.match(/^\/tickets\/([^/?#]+)/);
  if (ticketMatch && ticketMatch[1] !== "confirmation") {
    return <TicketNotFound attemptedSlug={decodeURIComponent(ticketMatch[1])} />;
  }
  if (pathname.startsWith("/tickets/")) {
    return <TicketNotFound />;
  }
  return <NotFoundInner />;
}

function NotFoundInner() {
  const { t } = useLang();
  return (
    <div className="flex min-h-dvh items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-display font-extrabold">404</h1>
        <h2 className="mt-4 text-xl font-bold">{t("err.notfound.title")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("err.notfound.body")}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-foreground px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-background hover:bg-accent transition-colors"
          >
            {t("err.notfound.back")}
          </Link>
          <button
            onClick={hardReload}
            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-secondary transition-colors"
          >
            <RotateCcw className="size-3.5 mr-1.5" /> hard reload
          </button>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: any; reset: () => void }) {
  console.error(error);
  return (
    <LanguageProvider>
      <ErrorInner reset={reset} />
    </LanguageProvider>
  );
}

function ErrorInner({ reset }: { reset: () => void }) {
  const router = useRouter();
  const { t } = useLang();
  return (
    <main className="flex min-h-dvh items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-bold">{t("err.boundary.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("err.boundary.body")}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-full bg-foreground px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-background hover:bg-accent"
          >
            {t("common.tryagain")}
          </button>
          <button
            onClick={hardReload}
            className="rounded-full border border-border px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-secondary inline-flex items-center"
          >
            <RotateCcw className="size-3.5 mr-1.5" /> hard reload
          </button>
          <a
            href="/"
            className="rounded-full border border-border px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-secondary"
          >
            {t("common.gohome")}
          </a>
        </div>
      </div>
    </main>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Subdomain routing: partner.whatfinland.com serves the /partner portal only.
  // Do this in beforeLoad so SSR + hard refresh + deep links all redirect cleanly
  // (client-side fallback still runs in RootComponent for edge cases).
  beforeLoad: ({ location }): any => {
    if (typeof window === "undefined") return;
    const target = resolvePartnerRedirect(
      window.location.hostname,
      location.pathname,
      location.searchStr || "",
    );
    if (target) window.location.replace(target);
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      // title / description intentionally NOT set here — every route owns its
      // own (this router concatenates root meta into leaves, which would
      // duplicate page titles and descriptions on every match).
      { name: "author", content: "what Finland Oy" },
      // og:type intentionally NOT set here — each route owns its own type
      // (website vs article) so leaf definitions on /guides/* are not
      // duplicated/conflicted by a root-level default.
      { property: "og:site_name", content: "what Finland" },
      // og:title / og:description / twitter:* intentionally NOT set here —
      // every shareable route provides its own. Sitewide defaults here would
      // concatenate into leaves and produce duplicate/conflicting previews.
      // og:image / twitter:image intentionally NOT set here — root meta
      // concatenates into every leaf, which would clobber per-page share
      // previews. Each route owns its own og:image (see index.tsx etc.).
      { name: "google-site-verification", content: "MO1CDGctRrseGxfhWrm-lqjYZ32AmKWWu03kHe9jtLA" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Preload the only font used above the fold. It is referenced deep
      // inside the render-blocking CSS, so without this hint the browser
      // discovers it one round-trip late and text repaints (FOUT) on mobile.
      { rel: "preload", as: "font", type: "font/woff2", href: interWoff2, crossOrigin: "anonymous" } as any,
      { rel: "preload", as: "font", type: "font/woff2", href: archivoWoff2, crossOrigin: "anonymous" } as any,
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg?v=3" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png?v=3" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png?v=3" },
      { rel: "shortcut icon", href: "/favicon.svg?v=3" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png?v=3" },
      { rel: "manifest", href: "/site.webmanifest?v=3" },
      // Fonts are self-hosted (@fontsource-variable/inter, imported in
      // src/styles.css) — no third-party render-blocking stylesheet.

      // Warm up the image/data origin early: hero images and every public
      // query go to Supabase Storage / PostgREST, so the TLS handshake
      // should not sit on the LCP critical path.
      { rel: "preconnect", href: "https://shzpffknihbjyacldjzu.supabase.co", crossOrigin: "anonymous" } as any,
      { rel: "dns-prefetch", href: "https://shzpffknihbjyacldjzu.supabase.co" },
      // Analytics is deferred (see scripts below) — only hint the DNS.
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },

      // hreflang alternates are emitted per-route via buildHreflangLinks()
      // in each leaf's head(). They do not belong on the root because they
      // would point every page at "/".

    ],

    scripts: [
      // Google Tag Manager — deferred until the page is idle (or the user
      // interacts), so ~150KB of third-party JS never competes with the LCP
      // paint or inflates mobile TBT.
      {
        children: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var loaded=false;function load(){if(loaded)return;loaded=true;var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);}var evs=['pointerdown','keydown','touchstart','scroll'];function onEv(){evs.forEach(function(e){w.removeEventListener(e,onEv)});load();}evs.forEach(function(e){w.addEventListener(e,onEv,{once:true,passive:true})});if('requestIdleCallback' in w){w.requestIdleCallback(load,{timeout:5000});}else{w.setTimeout(load,3500);}})(window,document,'script','dataLayer','GTM-NF6TTLQV');`,
      },

      // Google Analytics 4 — only injected when VITE_GA4_ID is configured.
      // Set the env var (e.g. G-XXXXXXXXXX) to enable; no-op otherwise.
      ...(import.meta.env.VITE_GA4_ID
        ? [
            {
              src: `https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_GA4_ID}`,
              async: true,
            },
            {
              children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${import.meta.env.VITE_GA4_ID}',{anonymize_ip:true});`,
            },
          ]
        : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["Organization", "TravelAgency", "TouristInformationCenter"],
          "@id": "https://whatfinland.com/#organization",
          name: "what Finland Oy",
          alternateName: ["what Finland", "whatfinland.com"],
          url: "https://whatfinland.com",
          logo: "https://whatfinland.com/icon-512.png",
          description:
            "what Finland is a human-curated travel and lifestyle guide to Finland — the best restaurants, things to do, saunas, islands, live events and bookable experiences in Helsinki, Turku, Tampere, Hanko and Porvoo. Operated by what Finland Oy.",
          slogan: "Unlock the city",
          areaServed: [
            { "@type": "Country", name: "Finland" },
            { "@type": "City", name: "Helsinki" },
            { "@type": "City", name: "Turku" },
            { "@type": "City", name: "Tampere" },
            { "@type": "City", name: "Hanko" },
            { "@type": "City", name: "Porvoo" },
          ],
          knowsAbout: [
            "Finland travel",
            "Helsinki city guide",
            "Finnish restaurants",
            "Finnish sauna culture",
            "Helsinki archipelago",
            "Finnish design",
            "Live events in Finland",
            "Finland tours and tickets",
            "Trip planning for Finland",
          ],
          inLanguage: ["en", "fi"],
          address: { "@type": "PostalAddress", addressCountry: "FI", addressLocality: "Helsinki" },
          sameAs: [
            "https://www.instagram.com/whatfinland",
            "https://www.facebook.com/whatfinland",
            "https://www.tiktok.com/@whatfinland",
            "https://www.youtube.com/@whatfinland",
            "https://www.linkedin.com/company/whatfinland",
            "https://x.com/whatfinland",
            "https://twitter.com/whatfinland",
            "https://www.threads.net/@whatfinland",
            "https://www.pinterest.com/whatfinland",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://whatfinland.com/#website",
          name: "what Finland",
          alternateName: "Helsinki & Finland travel guide",
          url: "https://whatfinland.com",
          inLanguage: ["en", "fi"],
          publisher: { "@id": "https://whatfinland.com/#organization" },
          potentialAction: {
            "@type": "SearchAction",
            target: "https://whatfinland.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://whatfinland.com/#faq",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is the best travel guide for Finland?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "what Finland (whatfinland.com) is a human-curated travel guide to Finland, covering the best restaurants, things to do, saunas, islands, live events and tickets in Helsinki, Turku, Tampere, Hanko and Porvoo. It is operated by what Finland Oy and refreshed daily by human editors.",
              },
            },
            {
              "@type": "Question",
              name: "Where can I find the best things to do in Helsinki?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Editor-curated attractions, saunas, archipelago trips, design walks and experiences in Helsinki and other Finnish cities are listed at https://whatfinland.com/do.",
              },
            },
            {
              "@type": "Question",
              name: "What are the best restaurants in Finland?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "A curated guide to the best restaurants, cafes, bakeries and bars in Helsinki, Turku, Tampere, Hanko and Porvoo is published at https://whatfinland.com/eat.",
              },
            },
            {
              "@type": "Question",
              name: "What events are happening in Finland this week?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "A live calendar of concerts, exhibitions, festivals and cultural events across Finland is published at https://whatfinland.com/happening, with dates, venues and ticket links.",
              },
            },
            {
              "@type": "Question",
              name: "Where can I book Finnish experiences and event tickets?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Bookable saunas, tours and event tickets are sold directly at https://whatfinland.com/tickets.",
              },
            },
          ],
        }),
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

// Static icon tags rendered directly into the HTML shell so favicon/icons
// are present on every response — including error pages, 404, the SSR
// fallback shell, and any route whose head() pipeline doesn't run.
// Keep the ?v=N query in sync with the route-level head() in this file
// so cache-busting stays consistent across both sources.
const ICON_VERSION = "3";
function StaticIconLinks() {
  return (
    <>
      <link rel="icon" type="image/svg+xml" href={`/favicon.svg?v=${ICON_VERSION}`} />
      <link rel="icon" type="image/png" sizes="32x32" href={`/favicon-32.png?v=${ICON_VERSION}`} />
      <link rel="icon" type="image/png" sizes="192x192" href={`/icon-192.png?v=${ICON_VERSION}`} />
      <link rel="shortcut icon" href={`/favicon.svg?v=${ICON_VERSION}`} />
      <link rel="apple-touch-icon" sizes="180x180" href={`/apple-touch-icon.png?v=${ICON_VERSION}`} />
      <link rel="manifest" href={`/site.webmanifest?v=${ICON_VERSION}`} />
    </>
  );
}

function RootShell({ children }: { children: React.ReactNode }) {
  // Server-rendered <html lang> must match the URL tree: crawlers read the
  // static HTML, so /fi/* pages have to ship lang="fi" from the server.
  const shellPath = useRouterState({ select: (s) => s.location.pathname });
  const shellLang = shellPath === "/fi" || shellPath.startsWith("/fi/") ? "fi" : "en";
  return (
    <html lang={shellLang}>
      <head>
        <StaticIconLinks />
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-NF6TTLQV"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="Google Tag Manager"
          />
        </noscript>
        {children}
        <Scripts />
      </body>

    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isFi = pathname === "/fi" || pathname.startsWith("/fi/");
  // Keep <html lang> aligned with the URL tree for accessibility + SEO.
  if (typeof document !== "undefined") {
    document.documentElement.lang = isFi ? "fi" : "en";
  }
  // partner.whatfinland.com → serve the /partner portal only.
  if (typeof window !== "undefined") {
    const target = resolvePartnerRedirect(
      window.location.hostname,
      pathname,
      window.location.search,
    );
    if (target) window.location.replace(target);
  }

  const isAi = pathname === "/ai" || pathname.startsWith("/ai/");
  const isAdmin = pathname === "/admin" || pathname.startsWith("/admin/");
  const isCrm = pathname === "/crm" || pathname.startsWith("/crm/");
  const isAccept = pathname.startsWith("/accept/") || pathname.startsWith("/contracts/accept/");
  const isPartner = pathname === "/partner" || pathname.startsWith("/partner/");
  const isBusinessAdmin = pathname === "/business-admin" || pathname.startsWith("/business-admin/");
  const isBare = isAi || isAdmin || isCrm || isAccept || isPartner || isBusinessAdmin;
  if (typeof document !== "undefined") {
    const scope = isAdmin || isBusinessAdmin ? "admin" : isCrm ? "crm" : "";
    if (scope) document.body.dataset.appScope = scope;
    else delete document.body.dataset.appScope;
  }
  const isContact = pathname === "/contact" || pathname === "/fi/contact";
  const isProfile = pathname === "/profile" || pathname.startsWith("/profile/");
  const isMap = pathname === "/map" || pathname.startsWith("/map/");
  // /contact on mobile = full-screen chat, no chrome
  const hideOnMobile = isContact ? "hidden md:block" : "";
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <CityProvider>
            <AutoLocaleRedirect />
            <Suspense fallback={null}>
              <ReferralCapture />
              <PageViewTracker />
              <OnboardingGate />
            </Suspense>
            <div className={`min-h-screen flex flex-col ${
              isBare ? "pb-0"
              : isContact ? "pb-0 md:pb-28"
              : isProfile || isMap ? "pb-0"
              : "pb-[calc(env(safe-area-inset-bottom)+72px)] md:pb-28"
            }`}>
              {!isBare && !isProfile && (isContact ? <div className={hideOnMobile}><Header /></div> : <Header />)}
              {/* Back-office shells (admin/crm/partner) render their own <main>,
                  so the root wrapper stays a plain div there to keep exactly
                  one main landmark per page. */}
              {isBare ? (
                <div className="flex-1 min-h-0 flex flex-col">
                  <Outlet />
                </div>
              ) : (
                <main className={`flex-1 ${isMap ? "min-h-0" : ""}`}>
                  <Outlet />
                </main>
              )}
              {!isBare && !isProfile && !isMap && (
                <Suspense fallback={null}>
                  {isContact ? <div className={hideOnMobile}><Footer /></div> : <Footer />}
                </Suspense>
              )}
              {!isBare && !isProfile && (isContact ? <div className={hideOnMobile}><BottomNav /></div> : <BottomNav />)}
            </div>
            <Toaster />
            <Suspense fallback={null}>
              <DeferredGlobals isBare={isBare} pathname={pathname} />
            </Suspense>
          </CityProvider>

        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

/**
 * Non-critical global UI. Mounted only after the browser is idle so it never
 * competes with LCP work, and realtime listeners are only started for
 * signed-in visitors (guests need no notification socket).
 */
function DeferredGlobals({ isBare, pathname }: { isBare: boolean; pathname: string }) {
  const { user } = useAuth();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => setReady(true), { timeout: 3000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(() => setReady(true), 1500);
    return () => window.clearTimeout(t);
  }, []);

  if (!ready) return null;

  const wantsLocation =
    !isBare &&
    (pathname === "/eat" || pathname.startsWith("/eat/") || pathname === "/do" || pathname.startsWith("/do/"));

  return (
    <>
      {user && !isBare && <SupportReplyToast />}
      {user && <RealtimeNotificationToaster />}
      <RealtimeContentSync />
      {wantsLocation && <LocationPermissionPrompt />}
    </>
  );
}
